<template>
  <div>
    <vue-simple-spinner v-if="!clubs.length" class="mb-10" :size="24" :line-size="2" line-bg-color="#D1865F" line-fg-color="#FFF" />
    
    <swiper v-else :options="options" ref="clubSwiper">
      <swiper-slide id="slide-start">
        <ol class="list-reset flex flex-wrap justify-around text-center max-w-xl mx-auto mb-12">
          <li v-for="(club, i) in clubs" :key="club.id" class="mb-8">
            <img 
              :src="`/assets/img/logos/clubs/club_${club.seal.value}.png`"
              data-sizes="auto"
              data-aspectratio="1/1"
              :data-srcset="
                `/assets/img/logos/clubs/club_${club.seal.value}.png 150w,
                /assets/img/logos/clubs/club_${club.seal.value}@2x.png 300w,
                /assets/img/logos/clubs/club_${club.seal.value}@3x.png 450w`"
              class="lazyload mb-4"
              :alt="club.title">
            
            <div class="flex justify-around font-sans">
              <p class="text-brown text-3xl leading-tight">{{ club.bottlesPerShipment }} <span class="block text-base">bottles</span></p>
              <p class="text-brown text-3xl leading-tight">{{ club.shipmentsPerYear }}x <span class="block text-base">per year</span></p>
            </div>
            
            <p class="text-brown text-3xl leading-tight font-sans">${{club.pricePerShipment.minimum}}-${{club.pricePerShipment.maximum}} <span class="block text-base">per shipment</span></p>
            <a :href="`/wine-club/join?club=${ encodeURIComponent(club.title) }`" class="btn my-6 text-orange-darker"  :aria-label="'Join ' + club.title">Join Now</a>
            <button @click="$refs.clubSwiper.swiper.slideTo(i+1)" class="block text-brown text-center mx-auto underline" :aria-label="'Learn more about ' + club.title">Learn More</button>
          </li>
        </ol>
      </swiper-slide>

      <swiper-slide 
        v-for="club in clubs"
        :key="'slide_' + club.id"
        :data-hash="'slide-' + club.seal.value"
        :id="'slide-' + club.seal.value"
        class="text-center"
        >
        
        <img 
          :src="`/assets/img/logos/clubs/club_${club.seal.value}.png`"
          data-sizes="auto"
          data-aspectratio="1/1"
          :data-srcset="
            `/assets/img/logos/clubs/club_${club.seal.value}.png 150w,
            /assets/img/logos/clubs/club_${club.seal.value}@2x.png 300w,
            /assets/img/logos/clubs/club_${club.seal.value}@3x.png 450w`"
          class="lazyload mb-4 max-w-xxs mx-auto"
          :alt="club.title + ' Icon'">

        <div class="flex justify-around font-sans max-w-sm mx-auto mb-4">
          <p class="text-brown text-3xl leading-tight">{{ club.bottlesPerShipment }} <span class="block text-base">bottles</span></p>
          <p class="text-brown text-3xl leading-tight font-sans">${{club.pricePerShipment.minimum}}-${{club.pricePerShipment.maximum}} <span class="block text-base">per shipment</span></p>
          <p class="text-brown text-3xl leading-tight">{{ club.shipmentsPerYear }}x <span class="block text-base">per year</span></p>
        </div>
        
        <div class="flex flex-wrap justify-center max-w-md mx-auto">
          <div class="flex-1">
            <h3 class="text-2xl text-brown">Mixed</h3>
            <div class="flex justify-center">
              <p class="flex items-center text-xl mr-4 mb-2"><svg class="bottle-dims mr-2 text-red-darkest"><use xlink:href="#bottle"></use></svg> x {{ club.mixedDetails.redWinesPerShipment }}</p>
              <p class="flex items-center text-xl mb-2"><svg class="bottle-dims mr-2 text-yellow"><use xlink:href="#bottle"></use></svg> x {{ club.mixedDetails.whiteWinesPerShipment }}</p>
            </div>
            <p class="italic text-sm mb-2">{{ club.mixedDetails.subHeader }}</p>
          </div>
          <div class="or_text font-sans">OR</div>
          <div class="flex-1">
            <h3 class="text-2xl text-brown">Reds Only</h3>
            <div class="flex justify-center">
              <p class="flex items-center text-xl mb-2"><svg class="bottle-dims mr-2 text-red-darkest"><use xlink:href="#bottle"></use></svg> x {{ club.redsOnlyDetails.redWinesPerShipment }}</p>
            </div>
            <p class="italic text-sm mb-2">{{ club.redsOnlyDetails.subHeader }}</p>
          </div>
        </div>

        <a :href="`/wine-club/join?club=${ encodeURIComponent(club.title) }`" class="btn my-10 text-orange-darkest" :aria-label="'Join ' + club.title">Join Now</a>

        <div class="bg-white-dark py-10 border-t border-grey-lightest">
          <h4 class="text-brown text-xl mb-8">{{club.seal.label}} Club Benefits</h4>
          <ul class="list-reset w-5/6 mx-auto cols text-md text-grey-darkest">
            <li 
              v-for="(bene, index) in club.clubBenefits" 
              :key="bene + index" 
              v-html="bene"></li>
          </ul>
        </div>
      </swiper-slide>

      <div v-if="clubs.length" class="swiper-prev absolute pin-l pin-t z-10 ml-2 md:mr-8" slot="button-prev">
        <svg class="left-caret-dims text-orange-darker"><use xlink:href="#left-caret"></use></svg>
      </div>
      
      <div v-if="clubs.length" class="swiper-next absolute pin-r pin-t z-10 mr-2 md:ml-8" slot="button-next">
        <svg class="right-caret-dims text-orange-darker"><use xlink:href="#right-caret"></use></svg>
      </div>
    </swiper>
  </div>
</template>

<script>
import axios from 'axios'
import VueSimpleSpinner from 'vue-simple-spinner';


export default {
  name: 'WineClubOverview',

  data() {
    return {
      clubs: [],
      formErrors: [],
      options: {
        autoHeight: true,
        roundLengths: true,
        grabCursor: true,
        observer: true,
        observeParents: true,
        hashNavigation: {
          watchState: true
        },
        navigation: {
          nextEl: '.swiper-next',
          prevEl: '.swiper-prev'
        },
        a11y: true
      }
    }
  },

  components: {
    VueSimpleSpinner
  },

  created() {
    axios.get('/api/v1/clubs')
    .then(response => {
      this.clubs = response.data.data
      this.$refs.clubSwiper.swiper.update();
    })
    .catch(e => {
      this.formErrors.push(e)
    })

    document.addEventListener('lazyloaded', (e) => {
      this.$refs.clubSwiper.swiper.update();
    });
  },

  updated() {
    const hash = document.location.hash.replace('#', '');
    const swiper = this.$refs.clubSwiper.swiper;

    if (hash) {      
      for (let i = 0, length = swiper.slides.length; i < length; i += 1) {
        const slide = swiper.slides.eq(i);
        const slideHash = slide.attr('data-hash');
        console.log(slide, slideHash);
        if (slideHash === hash && !slide.hasClass(swiper.params.slideDuplicateClass)) {
          const index = slide.index();
          swiper.slideTo(index, 0, swiper.params.runCallbacksOnInit, true);
          swiper.update()
        }
      }
    }

  }
}
</script>

<style>
  .swiper-button-disabled { opacity: 0.2 }

  .swiper-next,
  .swiper-prev {
    top: 20%;
  }

  .or_text {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 100%;
    padding: 0 10%;
  }

  .or_text::before,
  .or_text::after {
    background-color: #dfdbd9;
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    margin: 1rem 10px;
  }

  @media (min-width: 576px) {
    .or_text {
      flex-direction: column;
      width: auto;
      padding: 0;
      margin: 0 10px;
    }

    .or_text::before,
    .or_text::after {
      width: 1px;
      height: 100%;
      margin: 5px auto;
    }
  }

  .cols {
    columns: 2;
    column-rule: 1px solid #dfdbd9;
    column-gap: 40px;
    column-width: 300px;
  }

  @media (min-width: 1200px) {
    .cols {
      column-gap: 80px;
    }
  }

  .cols p { margin-bottom: 0.75rem; }
  .cols strong { color: #6D6661; }
</style>



// WEBPACK FOOTER //
// _js/components/WineClubOverview.vue